import React from 'react';
import { Facebook, Instagram, Twitter, Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'About Us', href: '#about' },
    { name: 'Tours', href: '#tours' },
    { name: 'Services', href: '#services' },
    { name: 'Gallery', href: '#gallery' },
    { name: 'Contact', href: '#contact' }
  ];

  const popularTours = [
    { name: 'Peninsula Package', href: '#tours' },
    { name: 'Safari Day Tour', href: '#tours' },
    { name: 'City Escape', href: '#tours' },
    { name: 'Wine Tasting', href: '#tours' }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info with Logo */}
          <div>
            <img 
              src="/cape-atlantic-logo.png" 
              alt="Cape Atlantic Tours" 
              className="h-20 w-auto object-contain mb-4"
              style={{ imageRendering: 'high-quality' }}
            />
            <p className="text-gray-400 mb-4 leading-relaxed">
              Your gateway to unforgettable Cape Town experiences. We showcase the best that Cape Town has to offer with professional, personalized service.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-800 p-2 rounded-full hover:bg-[#6B0B0B] transition-colors duration-300"
              >
                <Facebook size={20} />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-800 p-2 rounded-full hover:bg-[#6B0B0B] transition-colors duration-300"
              >
                <Instagram size={20} />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-800 p-2 rounded-full hover:bg-[#6B0B0B] transition-colors duration-300"
              >
                <Twitter size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-[#B22222]">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      if (link.href.startsWith('#')) {
                        e.preventDefault();
                        document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="text-gray-400 hover:text-[#B22222] transition-colors duration-300"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Popular Tours */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-[#B22222]">Popular Tours</h4>
            <ul className="space-y-2">
              {popularTours.map((tour) => (
                <li key={tour.name}>
                  <a
                    href={tour.href}
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector(tour.href)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-gray-400 hover:text-[#B22222] transition-colors duration-300"
                  >
                    {tour.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-[#B22222]">Legal</h4>
            <ul className="space-y-2">
              <li>
                <a href="/privacy" className="text-gray-400 hover:text-[#B22222] transition-colors duration-300">
                  Privacy Policy
                </a>
              </li>
              <li>
                <a href="/terms" className="text-gray-400 hover:text-[#B22222] transition-colors duration-300">
                  Terms of Service
                </a>
              </li>
              <li>
                <a href="/cancellation" className="text-gray-400 hover:text-[#B22222] transition-colors duration-300">
                  Cancellation Policy
                </a>
              </li>
              <li>
                <a href="/disclaimer" className="text-gray-400 hover:text-[#B22222] transition-colors duration-300">
                  Disclaimer
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-[#B22222]">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin size={20} className="text-[#B22222] flex-shrink-0 mt-1" />
                <span className="text-gray-400">
                  V&A Waterfront<br />
                  Cape Town<br />
                  Western Cape<br />
                  South Africa
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone size={20} className="text-[#B22222] flex-shrink-0" />
                <a href="https://wa.me/27736540090" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#B22222]">
                  073 654 0090 (WhatsApp)
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail size={20} className="text-[#B22222] flex-shrink-0" />
                <a href="mailto:info@capeatlantictours.com" className="text-gray-400 hover:text-[#B22222]">
                  info@capeatlantictours.com
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © {currentYear} Cape Atlantic Tours. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <a href="#" className="text-gray-400 hover:text-[#B22222] transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-400 hover:text-[#B22222] transition-colors">
                Terms of Service
              </a>
              <a href="#" className="text-gray-400 hover:text-[#B22222] transition-colors">
                Cancellation Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
